import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { Building2, Edit, FileDown, Plus, Power, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Customer, ListStatusFilter, ListTag } from '../../types/lists.types';
import { customersApi } from '../../utils/lists-api';
import { cn } from '../../components/ui/utils';
import { TAG_PILL_CLASS, externalHref, orDash } from './utils/lists-utils';
import StatusPill from './components/StatusPill';
import CustomerFormDialog from './components/CustomerFormDialog';

export default function CustomersPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.LIST_CUSTOMER);

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [categories, setCategories] = useState<ListTag[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ListStatusFilter>('active');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Customer | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [rows, tags] = await Promise.all([
                customersApi.list({ status }),
                customersApi.categories(),
            ]);
            setCustomers(rows);
            setCategories(tags);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load customers');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        if (canRead) fetchData();
    }, [canRead, fetchData]);

    // Search filters client-side so typing doesn't refetch on every keystroke.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return customers;
        return customers.filter((c) =>
            [c.tradingName, c.legalName, c.contactName, c.email]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [customers, search]);

    const handleToggleStatus = async (customer: Customer) => {
        try {
            await customersApi.setStatus(customer.id, !customer.isActive);
            toast.success(`${customer.tradingName} set to ${customer.isActive ? 'Inactive' : 'Active'}`);
            fetchData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update status');
        }
    };

    const handleExport = async (format: 'csv' | 'excel') => {
        try {
            await customersApi.export(format);
            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch {
            toast.error('Failed to generate export file');
        }
    };

    const openAdd = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (customer: Customer) => { setEditing(customer); setDialogOpen(true); };

    if (!canRead) return <AccessRestricted />;

    const categoryPills = (customer: Customer) =>
        customer.categories.length === 0 ? (
            <span className="text-muted-foreground">–</span>
        ) : (
            <div className="flex flex-wrap gap-1">
                {customer.categories.map((c) => (
                    <span key={c.id} className={TAG_PILL_CLASS}>{c.name}</span>
                ))}
            </div>
        );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Customers</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Shared customer records used across the app.
                    </p>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
                    <Can I={ACTIONS.EXPORT} a={RESOURCES.LIST_CUSTOMER}>
                        <Button
                            variant="outline"
                            onClick={() => handleExport('csv')}
                            className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
                        >
                            <FileDown size={16} /><span>Export CSV</span>
                        </Button>
                    </Can>
                    <Can I={ACTIONS.CREATE} a={RESOURCES.LIST_CUSTOMER}>
                        <Button
                            onClick={openAdd}
                            className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            <Plus size={16} /><span>Add Customer</span>
                        </Button>
                    </Can>
                </div>
            </div>

            {/* Active / Inactive tabs */}
            <Tabs value={status} onValueChange={(v) => setStatus(v as ListStatusFilter)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="active" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Active
                    </TabsTrigger>
                    <TabsTrigger value="inactive" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Inactive
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Search */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search customers..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No customers found</div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((c) => (
                            <Card key={c.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={16} className="text-muted-foreground shrink-0" />
                                            <span className="font-medium truncate">{c.tradingName}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">{c.legalName}</p>
                                    </div>
                                    <StatusPill isActive={c.isActive} />
                                </div>
                                <div className="mt-3 space-y-1 text-sm">
                                    <p className="text-muted-foreground">{c.state} · {orDash(c.contactName)}</p>
                                    <p className="text-muted-foreground">{orDash(c.email)}</p>
                                    <div className="pt-1">{categoryPills(c)}</div>
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_CUSTOMER}>
                                        <Button variant="outline" size="icon" onClick={() => openEdit(c)} aria-label="Edit">
                                            <Edit size={16} />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleToggleStatus(c)}
                                            aria-label={c.isActive ? 'Set inactive' : 'Set active'}
                                            className={cn(c.isActive && 'text-destructive')}
                                        >
                                            <Power size={16} />
                                        </Button>
                                    </Can>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Trading Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Legal Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">State</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Contact</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Email</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Category</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((c) => (
                                        <TableRow key={c.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={16} className="text-muted-foreground" />
                                                    {c.tradingName}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">{c.legalName}</TableCell>
                                            <TableCell>{c.state}</TableCell>
                                            <TableCell>{orDash(c.contactName)}</TableCell>
                                            <TableCell>
                                                {c.email ? (
                                                    <a href={`mailto:${c.email}`} className="hover:underline text-primary">
                                                        {c.email}
                                                    </a>
                                                ) : (
                                                    <span className="text-muted-foreground">–</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[220px]">
                                                {categoryPills(c)}
                                            </TableCell>
                                            <TableCell><StatusPill isActive={c.isActive} /></TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    {c.website && (
                                                        <a
                                                            href={externalHref(c.website)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-primary hover:underline self-center"
                                                        >
                                                            Site
                                                        </a>
                                                    )}
                                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_CUSTOMER}>
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(c)} aria-label="Edit">
                                                            <Edit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => handleToggleStatus(c)}
                                                            aria-label={c.isActive ? 'Set inactive' : 'Set active'}
                                                            className={cn(c.isActive && 'text-destructive')}
                                                        >
                                                            <Power size={16} />
                                                        </Button>
                                                    </Can>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <CustomerFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                customer={editing}
                categories={categories}
                onSaved={fetchData}
            />
        </div>
    );
}
